'use client';
import { useRef } from 'react';
import { VIDEO_VARIANTS } from '@workspace/constants';
import { useVimeoPlayerWithAutoPlay } from './hooks';
import { VideoImageData } from '@workspace/interfaces';
import { getFinalValues } from '@/app/lib/media-helper';
import { processHeading } from '@/app/lib/heading-utils';
import { VideoWithText } from './variants/VideoWithText';
import { VideoWithoutText } from './variants/VideoWithoutText';
import { ImageVideoWithText } from './variants/ImageVideoWithText';

interface AppVideoImageProps extends VideoImageData {
  wrapperClassName?: string;
  shouldRenderOverlay?: boolean;
}

export function AppVideoImage(props: AppVideoImageProps) {
  const { variant, video, wrapperClassName, shouldRenderOverlay = true } = props;

  const playerContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    thumbnail: finalThumbnail,
    heading: finalHeading,
    description: finalDescription,
    buttons: finalButtons,
    isVideo: isBackgroundVideo,
  } = getFinalValues(variant, props);

  const { text: headingText } = processHeading(finalHeading);

  const { isPlaying, duration, play, pause, isReady, wrapperRef, aspectRatio } = useVimeoPlayerWithAutoPlay({
    video,
    variant,
    isBackgroundVideo: !!isBackgroundVideo,
    containerRef: playerContainerRef,
  });

  switch (variant) {
    case VIDEO_VARIANTS.VideoWithText:
      return (
        <VideoWithText
          wrapperRef={wrapperRef}
          playerContainerRef={playerContainerRef}
          thumbnail={finalThumbnail}
          heading={headingText}
          description={finalDescription}
          duration={duration}
          isPlaying={isPlaying}
          isReady={isReady}
          onPlay={play}
          wrapperClassName={wrapperClassName}
          aspectRatio={aspectRatio ?? undefined}
        />
      );

    case VIDEO_VARIANTS.VideoWithoutText:
      return (
        <VideoWithoutText
          wrapperRef={wrapperRef}
          playerContainerRef={playerContainerRef}
          thumbnail={finalThumbnail}
          isPlaying={isPlaying}
          onPlay={play}
          onPause={pause}
          wrapperClassName={wrapperClassName}
          aspectRatio={aspectRatio ?? undefined}
        />
      );

    case VIDEO_VARIANTS.ImageVideoWithText:
      return (
        <ImageVideoWithText
          wrapperRef={wrapperRef}
          playerContainerRef={playerContainerRef}
          thumbnail={finalThumbnail}
          heading={headingText}
          description={finalDescription}
          buttons={finalButtons ?? undefined}
          isBackgroundVideo={!!isBackgroundVideo}
          isPlaying={isPlaying}
          onPlay={play}
          wrapperClassName={wrapperClassName}
          shouldRenderOverlay={shouldRenderOverlay}
          aspectRatio={aspectRatio ?? undefined}
        />
      );

    default:
      return null;
  }
}
